import { buildPurchaseTaxBreakdown, TaxTreatment } from '@longy/common'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { formatPrice } from '../../utils/formatPrice'

const TaxRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between gap-4 text-sm">
        <span className="text-brand-gray">{label}</span>
        <span className="text-right font-semibold">{value}</span>
    </div>
)

/**
 * The same tax rows the app's purchase summary shows, from the same pure breakdown and the same
 * translation keys — so a buyer who checks out on the web reads the treatment exactly as the app
 * states it, rather than the hardcoded Hungarian sentence the web checkout used to print.
 *
 * Renders nothing when the item carries no treatment, is free, or belongs to a mixed basket, which
 * is what `buildPurchaseTaxBreakdown` answers null for. This is the one surface that states a tax
 * treatment at all — a catalogue or a detail screen shows the gross price and nothing else.
 */
export const TaxBreakdown = ({
    tax,
    totalPrice,
    className,
}: {
    tax: TaxTreatment | null | undefined
    totalPrice: number | undefined
    className?: string
}) => {
    const { t } = useTranslation()

    const breakdown = buildPurchaseTaxBreakdown(totalPrice, tax)

    if (!breakdown) return null

    return (
        <div className={className}>
            {match(breakdown)
                .with({ kind: 'vat' }, ({ rate, net, vat, gross }) => (
                    <>
                        <TaxRow label={t('app.payment.netPrice')} value={formatPrice(net)} />
                        <TaxRow
                            label={t('app.payment.vatContent')}
                            value={t('app.payment.vatContentValue', {
                                rate,
                                amount: formatPrice(vat),
                            })}
                        />
                        <TaxRow label={t('app.payment.grossAmount')} value={formatPrice(gross)} />
                    </>
                ))
                /*
                 * The exemption is named here and nowhere else. A catalogue price says nothing
                 * about its tax, but the summary is the last screen before payment and stands in
                 * for the invoice, which has to carry the code the exemption is claimed under.
                 * The gross row stays beside it: the code explains why there is no VAT line, not
                 * what is being charged.
                 */
                .with({ kind: 'exempt' }, ({ exemptType, gross }) => (
                    <>
                        <TaxRow
                            label={t('app.payment.taxTreatment')}
                            value={
                                exemptType === 'objective'
                                    ? t('app.payment.objectiveExemptCode')
                                    : t('app.payment.subjectiveExemptCode')
                            }
                        />
                        <TaxRow label={t('app.payment.grossAmount')} value={formatPrice(gross)} />
                    </>
                ))
                /*
                 * The margin scheme has no code to name and no split to show — its VAT is on the
                 * commission, not on the ticket — so the total stands on its own.
                 */
                .with({ kind: 'margin' }, ({ gross }) => (
                    <TaxRow label={t('app.payment.grossAmount')} value={formatPrice(gross)} />
                ))
                .exhaustive()}
        </div>
    )
}
